/**
 * External viewport store — single source of truth for pan/zoom/focal.
 *
 * Replaces the old `useState<Viewport>` + manually-mirrored `vpRef`
 * pair. That pattern required every gesture handler to remember to write
 * `vpRef.current = next` alongside `setViewport(next)`; a missed write
 * meant a stale synchronous read on the next event in the same frame.
 *
 * An external store fixes both problems at once:
 *  - `get()` is the synchronous read the ref used to provide, but it can
 *    never drift from what `subscribe` notifies — they read the same
 *    `value`.
 *  - Handlers compute the next viewport from `get()` and commit with
 *    `set()`. No functional-updater dance, and any DOM measurement /
 *    rebase math runs in the handler (a normal side-effecting context)
 *    rather than inside a React state updater (which must stay pure).
 *
 * A single store instance can also be shared by two canvases (the diff
 * mirror), which previously required imperative `setViewportImmediate`
 * plumbing.
 */
import { useSyncExternalStore } from "react";
import type { Viewport } from "@archlang/scene/board";

export interface ViewportStore {
  /** Synchronous current value — always consistent with `subscribe`. */
  get(): Viewport;
  /** Replace the value (or read-modify-write via an updater). No-op if
   *  the result is referentially identical to the current value. */
  set(next: Viewport | ((current: Viewport) => Viewport)): void;
  subscribe(onChange: () => void): () => void;
}

export function createViewportStore(initial: Viewport): ViewportStore {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set(next) {
      const v =
        typeof next === "function"
          ? (next as (c: Viewport) => Viewport)(value)
          : next;
      if (v === value) return;
      value = v;
      for (const l of listeners) l();
    },
    subscribe(onChange) {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
  };
}

/** Subscribe a component to the store's viewport value. */
export function useViewportValue(store: ViewportStore): Viewport {
  return useSyncExternalStore(store.subscribe, store.get, store.get);
}
